import { useState, useEffect } from "react";
import { GenerateVizaButton } from "./GenerateVizaButton";
import { useForm } from "react-hook-form";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Loader2, FileText, Check, Calculator, AlertCircle, CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { patientApi } from "../services/api";
import { toast } from "sonner";
import { formatDate } from "../lib/utils";

// Funcție pentru calcularea vârstei din data nașterii
const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Interpretare IMC pentru adulți
const getIMCInterpretation = (imc) => {
  if (imc < 18.5) return { text: "Subponderal", color: "text-amber-600" };
  if (imc < 25) return { text: "Normal", color: "text-emerald-600" };
  if (imc < 30) return { text: "Supraponderal", color: "text-amber-600" };
  return { text: "Obezitate", color: "text-red-600" };
};

// Recomandare nutriție bazată pe IMC (și vârstă pentru obezitate juvenilă <18)
const getNutritieRecommendation = (imc, age) => {
  if (!imc || imc <= 0) return "";
  if (imc < 20) {
    return "Alimentație supravegheată controlată, hiperprotidică cu proteine de calitate superioară cu conținut redus de grăsimi asociate pentru creșterea masei musculare, normoglucidolipidică, vitaminizată bogată în fructe, legume, etc.";
  }
  if (imc >= 20 && imc <= 25) {
    return "Alimentație supravegheată normo-protidică, cu proteine de calitate superioară cu conținut de grăsimi asociate redus (pentru păstrarea masei active) normoglucidolipidică vitaminizată.";
  }
  if (imc > 25 && imc <= 30) {
    return "MONITORIZARE GREUTATE. Alimentație supravegheată, normoprotidică cu proteine de calitate superioară, cu conținut redus de grăsimi asociate, moderat hipoglucidolipidică, vitaminizată natural, bogată în fructe și legume proaspete, suc natural (fără zahăr, fosfați, cafeină), cereale integrale, fibre. Aport corespunzător de lichide (corelat cu pierderile, alimentația și parametrii biologici).";
  }
  // IMC > 30
  if (age && age < 18) {
    return "Examen endocrinologic, consult nutriționist, alimentație supravegheată controlată, normoproteică, accentuat hipoglucidolipidică cu evitarea grăsimilor hidrogenate și scăderea drastică a glucidelor rafinate pentru scăderea țesutului adipos, vitaminizată natural, bogată în fructe și legume proaspete, suc natural (fără zahăr, fosfați, cafeină).";
  }
  // IMC > 30 adult — generic obesitate
  return "MONITORIZARE GREUTATE STRICTĂ. Consult nutriționist recomandat. Alimentație supravegheată, normoprotidică, hipoglucidolipidică cu evitarea grăsimilor hidrogenate și a glucidelor rafinate, vitaminizată natural, bogată în fructe și legume proaspete, fibre. Aport corespunzător de lichide.";
};


// Interpretare IMC pentru copii/adolescenți (simplificată)
const getIMCInterpretationYouth = (imc, age, sex) => {
  const isMale = sex === "Masculin";
  let p5, p85, p95;
  if (age <= 10) {
    p5 = isMale ? 13.5 : 13.2;
    p85 = isMale ? 18.5 : 19.0;
    p95 = isMale ? 20.5 : 21.0;
  } else if (age <= 15) {
    p5 = isMale ? 15.0 : 15.5;
    p85 = isMale ? 22.5 : 24.0;
    p95 = isMale ? 26.0 : 27.5;
  } else {
    p5 = isMale ? 17.0 : 17.5;
    p85 = isMale ? 24.5 : 25.5;
    p95 = isMale ? 28.0 : 29.0;
  }
  if (imc < p5) return { text: `Subponderal (sub percentila 5 OMS)`, color: "text-amber-600" };
  if (imc < p85) return { text: `Normal (între percentila 5-85 OMS)`, color: "text-emerald-600" };
  if (imc < p95) return { text: `Risc suprapondere (între percentila 85-95 OMS)`, color: "text-amber-600" };
  return { text: `Supraponderal/Obezitate (peste percentila 95 OMS)`, color: "text-red-600" };
};

// Generare recomandare bazată pe sport
const generateSportRecommendation = (sport, greutateOptima, greutateActuala, taProcent, sex) => {
  const diferenta = greutateActuala - greutateOptima;
  const sportLower = (sport || "").toLowerCase();
  let recomandare = "";
  if (Math.abs(diferenta) < 2) {
    recomandare = `Greutatea actuală este în intervalul optim pentru performanță sportivă. `;
  } else if (diferenta > 0) {
    recomandare = `Se recomandă o reducere de aproximativ ${diferenta.toFixed(1)} kg pentru atingerea greutății optime. `;
  } else {
    recomandare = `Se recomandă o creștere de aproximativ ${Math.abs(diferenta).toFixed(1)} kg pentru atingerea greutății optime. `;
  }
  if (sportLower.includes("fotbal")) {
    recomandare += `Pentru fotbal, un TA% între ${sex === "Masculin" ? "8-12%" : "15-20%"} este ideal pentru viteză și rezistență.`;
  } else if (sportLower.includes("atletism") || sportLower.includes("alergare")) {
    recomandare += `Pentru atletism, masa musculară optimă și un TA% scăzut (${sex === "Masculin" ? "6-10%" : "12-18%"}) sunt esențiale.`;
  } else if (sportLower.includes("înot") || sportLower.includes("natatie")) {
    recomandare += `Pentru înot, un echilibru între masă musculară și flotabilitate este important. TA% recomandat: ${sex === "Masculin" ? "8-14%" : "16-22%"}.`;
  } else if (sportLower.includes("gimnastică") || sportLower.includes("gimnastica")) {
    recomandare += `Pentru gimnastică, raportul forță/greutate este crucial. TA% recomandat: ${sex === "Masculin" ? "5-10%" : "12-18%"}.`;
  } else if (sportLower.includes("box") || sportLower.includes("lupte") || sportLower.includes("judo")) {
    recomandare += `Pentru sporturi de contact, menținerea categoriei de greutate cu masă musculară maximă este prioritară.`;
  } else if (sportLower.includes("baschet") || sportLower.includes("volei") || sportLower.includes("handbal")) {
    recomandare += `Pentru sporturi de echipă, un echilibru între forță, viteză și rezistență este necesar. TA% recomandat: ${sex === "Masculin" ? "8-14%" : "16-22%"}.`;
  } else if (sportLower.includes("tenis")) {
    recomandare += `Pentru tenis, agilitatea și rezistența sunt esențiale. TA% recomandat: ${sex === "Masculin" ? "8-12%" : "15-20%"}.`;
  } else if (sportLower.includes("ciclism")) {
    recomandare += `Pentru ciclism, raportul putere/greutate este critic. TA% recomandat: ${sex === "Masculin" ? "6-12%" : "14-20%"}.`;
  } else {
    recomandare += `Pentru performanță optimă, se recomandă menținerea unui TA% între ${sex === "Masculin" ? "10-15%" : "18-25%"}.`;
  }
  return recomandare;
};

const getDefaultValues = (atelierData) => ({
  completat: atelierData?.completat || false,
  data_completare: atelierData?.data_completare || "",
  talie: atelierData?.talie || "",
  greutate: atelierData?.greutate || "",
  bust: atelierData?.bust || "",
  anvergura: atelierData?.anvergura || "",
  mobilitate: atelierData?.mobilitate || "",
  pl_abdomen: atelierData?.pl_abdomen || "",
  pl_flanc: atelierData?.pl_flanc || "",
  pl_spate: atelierData?.pl_spate || "",
  pl_brat: atelierData?.pl_brat || "",
  pl_coapsa: atelierData?.pl_coapsa || "",
  f_flexori_dr: atelierData?.f_flexori_dr || "",
  f_flexori_stg: atelierData?.f_flexori_stg || "",
  f_scapulara: atelierData?.f_scapulara || "",
  f_lombara: atelierData?.f_lombara || "",
  // Evaluare posturală
  tulburari_statica: atelierData?.tulburari_statica || "",
  tulb_atitudine_cifotica: atelierData?.tulb_atitudine_cifotica || false,
  tulb_cifoza: atelierData?.tulb_cifoza || false,
  tulb_atitudine_scoliotica: atelierData?.tulb_atitudine_scoliotica || false,
  tulb_scolioza: atelierData?.tulb_scolioza || false,
  tulb_atitudine_cifoscoliotica: atelierData?.tulb_atitudine_cifoscoliotica || false,
  tulb_cifoscolioza: atelierData?.tulb_cifoscolioza || false,
  tulb_atitudine_hiperlordotica: atelierData?.tulb_atitudine_hiperlordotica || false,
  tulb_hiperlordoza: atelierData?.tulb_hiperlordoza || false,
  asimetrie_umeri: atelierData?.asimetrie_umeri || "",
  asimetrie_torace: atelierData?.asimetrie_torace || "",
  at_dozare_vit_d: atelierData?.at_dozare_vit_d || false,
  at_activitate_adaptata: atelierData?.at_activitate_adaptata || false,
  at_optimizare_calciu: atelierData?.at_optimizare_calciu || false,
  asimetrie_membre_inf: atelierData?.asimetrie_membre_inf || "",
  asimetrie_membre_inf_tip: atelierData?.asimetrie_membre_inf_tip || "",
  asimetrie_membre_inf_stang: atelierData?.asimetrie_membre_inf_stang || "",
  asimetrie_membre_inf_drept: atelierData?.asimetrie_membre_inf_drept || "",
  mi_stang_mai_mare: atelierData?.mi_stang_mai_mare || false,
  mi_drept_mai_mare: atelierData?.mi_drept_mai_mare || false,
  genu_valgum: atelierData?.genu_valgum || "",
  genu_varum: atelierData?.genu_varum || "",
  picior_addus: atelierData?.picior_addus || "",
  picior_addus_grad: atelierData?.picior_addus_grad || "",
  recomandari_posturale: atelierData?.recomandari_posturale || "",
  // Rezultate
  imc: atelierData?.imc || "",
  imc_interpretare: atelierData?.imc_interpretare || "",
  ta_procent: atelierData?.ta_procent || "",
  ma_procent: atelierData?.ma_procent || "",
  ta_kg: atelierData?.ta_kg || "",
  ma_kg: atelierData?.ma_kg || "",
  greutate_optima: atelierData?.greutate_optima || "",
  recomandare_sport: atelierData?.recomandare_sport || "",
  recomandare_nutritie: atelierData?.recomandare_nutritie || "",
  i_flexori: atelierData?.i_flexori || "",
  i_flexori_valid: atelierData?.i_flexori_valid || false,
  i_scapulara: atelierData?.i_scapulara || "",
  i_scapulara_valid: atelierData?.i_scapulara_valid || false,
  i_lombara: atelierData?.i_lombara || "",
  i_lombara_valid: atelierData?.i_lombara_valid || false,
  i_fg: atelierData?.i_fg || "",
  i_fma: atelierData?.i_fma || "",
  i_fma_interpretare: atelierData?.i_fma_interpretare || "",
  i_fma_status: atelierData?.i_fma_status || "",
  observatii: atelierData?.observatii || "",
});

export const Atelier1Button = ({ 
  patientId, 
  patientData,
  atelierData, 
  onUpdate 
}) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  // Colaps „Forță Musculară" pentru sportivi sub 13 ani (implicit închis; admin poate deschide manual)
  const [showForta, setShowForta] = useState(true);

  const sex = patientData?.date_personale?.sex || "";
  const dataNasterii = patientData?.date_personale?.data_nasterii || "";
  const sport = patientData?.info_sportive?.sport_practicat || "";
  const age = calculateAge(dataNasterii);
  const isUnder13 = age !== null && age !== undefined && age < 13;

  // Sincronizează starea de colaps a „Forță Musculară" cu vârsta pacientului (auto-închis <13 ani)
  useEffect(() => {
    setShowForta(!isUnder13);
  }, [isUnder13]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
  } = useForm({
    defaultValues: getDefaultValues(atelierData),
  });

  const completat = watch("completat");
  const watchedValues = watch();

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      reset(getDefaultValues(atelierData));
    }
  };

  const calculateResults = () => {
    setIsCalculating(true);
    
    const values = getValues();
    const talie = parseFloat(values.talie) || 0;
    const greutate = parseFloat(values.greutate) || 0;
    const plAbdomen = parseFloat(values.pl_abdomen) || 0;
    const plFlanc = parseFloat(values.pl_flanc) || 0;
    const plSpate = parseFloat(values.pl_spate) || 0;
    const plBrat = parseFloat(values.pl_brat) || 0;
    const plCoapsa = parseFloat(values.pl_coapsa) || 0;
    const fFlexoriDr = parseFloat(values.f_flexori_dr) || 0;
    const fFlexoriStg = parseFloat(values.f_flexori_stg) || 0;
    const fScapulara = parseFloat(values.f_scapulara) || 0;
    const fLombara = parseFloat(values.f_lombara) || 0;

    if (talie <= 0 || greutate <= 0) {
      toast.error("Introduceți talia și greutatea pentru a calcula rezultatele");
      setIsCalculating(false);
      return;
    }

    // ========== COMPOZIȚIE CORPORALĂ ==========
    
    // IMC = Greutate / Talie² (talia în metri)
    const imc = greutate / (talie * talie);
    setValue("imc", imc.toFixed(2));
    
    // Recomandare nutriție bazată pe IMC + vârstă (independent de pliuri)
    setValue("recomandare_nutritie", getNutritieRecommendation(imc, age));

    // Interpretare IMC
    let imcInterpretare;
    if (age && age < 18) {
      imcInterpretare = getIMCInterpretationYouth(imc, age, sex);
    } else {
      imcInterpretare = getIMCInterpretation(imc);
    }
    setValue("imc_interpretare", imcInterpretare.text);

    // Suma pliurilor
    const sumaPliuri = plAbdomen + plFlanc + plSpate + plBrat + plCoapsa;
    const talieCm = talie * 100;
    
    // TA%, MA%, TAkg, MAkg, Greutate Optimă — calculate DOAR dacă pliurile cutanate sunt completate
    // (toate cele 5 pliuri trebuie să aibă cel puțin o valoare introdusă)
    const pliuriCompletate = plAbdomen > 0 && plFlanc > 0 && plSpate > 0 && plBrat > 0 && plCoapsa > 0;
    // MAkg — necesar pentru I.F.M.A. Se calculează DOI feluri:
    //  1) Din pliuri (precis) când pliurile sunt completate.
    //  2) Fallback estimat pe baza sexului (masculin 87%, feminin 83%) — permite calcul I.F.M.A
    //     pentru toată lumea, chiar dacă pliurile lipsesc (cererea medicului).
    const defaultMaPct = sex === "Masculin" ? 87 : 83;
    let maKg = greutate > 0 ? greutate * (defaultMaPct / 100) : 0;
    
    if (pliuriCompletate) {
      // TA% = (suma pliuri * 0.15) + 5.8 + (0.007184 * Greutate^0.425 * Talie(cm)^0.725)
      const suprafataCorporala = 0.007184 * Math.pow(greutate, 0.425) * Math.pow(talieCm, 0.725);
      const taProcent = (sumaPliuri * 0.15) + 5.8 + suprafataCorporala;
      setValue("ta_procent", taProcent.toFixed(2));

      // MA% = 100 - TA%
      const maProcent = 100 - taProcent;
      setValue("ma_procent", maProcent.toFixed(2));

      // TAkg = (Greutate * TA%) / 100
      const taKg = (greutate * taProcent) / 100;
      setValue("ta_kg", taKg.toFixed(2));

      // MAkg = Greutate - TAkg (recalculat precis din pliuri, suprascrie fallback-ul)
      maKg = greutate - taKg;
      setValue("ma_kg", maKg.toFixed(2));

      // Greutate optimă
      let greutateOptima;
      if (sex === "Masculin") {
        greutateOptima = maKg / 0.87;
      } else {
        greutateOptima = maKg / 0.83;
      }
      setValue("greutate_optima", greutateOptima.toFixed(2));

      // Generare recomandare sport
      let recomandare = generateSportRecommendation(sport, greutateOptima, greutate, taProcent, sex);
      // Pentru juniori sub 16 ani cu IMC ≥ 30 — alertă medicală obligatorie
      if (age && age < 16 && imc >= 30) {
        recomandare = `⚠️ Se recomandă control endocrinologie și control nutriție (sportiv sub 16 ani cu IMC = ${imc.toFixed(2)}). ` + recomandare;
      }
      setValue("recomandare_sport", recomandare);
    } else {
      // Pliurile NU sunt completate → NU afișăm TA%/MA%/Greutate optimă (acestea depind strict de pliuri)
      setValue("ta_procent", "");
      setValue("ma_procent", "");
      setValue("ta_kg", "");
      setValue("ma_kg", "");
      setValue("greutate_optima", "");
      // Recomandarea sport depinde de greutate optimă → o setăm doar cu alerta dacă e cazul
      if (age && age < 16 && imc >= 30) {
        setValue(
          "recomandare_sport",
          `⚠️ Se recomandă control endocrinologie și control nutriție (sportiv sub 16 ani cu IMC = ${imc.toFixed(2)}). Completați pliurile cutanate pentru calculul complet al compoziției corporale.`
        );
      } else {
        setValue(
          "recomandare_sport",
          "Pentru calculul Greutății Optime, TA% și MA%, completați toate cele 5 pliuri cutanate (Abdomen, Flanc, Spate, Braț, Coapsă)."
        );
      }
    }

    // ========== INDICI FORȚĂ MUSCULARĂ ==========
    
    // I.Flexori = ((F.Flexori Dr + F.Flexori Stg) / 2 / Greutate) * 100
    const iFlexori = ((fFlexoriDr + fFlexoriStg) / 2 / greutate) * 100;
    setValue("i_flexori", iFlexori.toFixed(2));
    
    // Validare I.Flexori: Masculin >= 65%, Feminin >= 50%
    const iFlexoriValid = sex === "Masculin" ? iFlexori >= 65 : iFlexori >= 50;
    setValue("i_flexori_valid", iFlexoriValid);

    // I.Scapulara = (F.Scapulara / Greutate) * 100
    const iScapulara = (fScapulara / greutate) * 100;
    setValue("i_scapulara", iScapulara.toFixed(2));
    
    // Validare I.Scapulara: Masculin >= 55%, Feminin >= 40%
    const iScapularaValid = sex === "Masculin" ? iScapulara >= 55 : iScapulara >= 40;
    setValue("i_scapulara_valid", iScapularaValid);

    // I.Lombara = (F.Lombara / Greutate) * 100
    const iLombara = (fLombara / greutate) * 100;
    setValue("i_lombara", iLombara.toFixed(2));
    
    // Validare I.Lombara: Masculin >= 190%, Feminin >= 130%
    const iLombaraValid = sex === "Masculin" ? iLombara >= 190 : iLombara >= 130;
    setValue("i_lombara_valid", iLombaraValid);

    // I.F.G = (F.Flexori Dr + F.Flexori Stg + F.Scapulara + F.Lombara) / 4 / Greutate
    const sumaForte = fFlexoriDr + fFlexoriStg + fScapulara + fLombara;
    const iFg = (sumaForte / 4) / greutate;
    setValue("i_fg", iFg.toFixed(2));

    // I.F.M.A = (suma forțe) / MAkg — SE CALCULEAZĂ ÎNTOTDEAUNA, chiar dacă
    // pliurile cutanate nu au fost completate. MAkg este estimat cu default sex-based
    // când pliurile lipsesc (masculin 87%, feminin 83% din greutate); când sunt
    // completate, MAkg e recalculat precis mai sus.
    const iFma = maKg > 0 ? sumaForte / maKg : 0;
    setValue("i_fma", maKg > 0 ? iFma.toFixed(2) : "");

    // Interpretare I.F.M.A — aceleași praguri clinice indiferent dacă MAkg vine din
    // pliuri sau fallback (medicul a cerut acest comportament pentru toți sportivii).
    let iFmaInterpretare = "";
    let iFmaStatus = "";

    if (maKg > 0) {
      if (iFg >= 1 && iFma >= 1.2) {
        iFmaInterpretare = "Nivel optim de pregătire";
        iFmaStatus = "optim";
      } else if (iFg < 1 && iFma >= 1.2) {
        iFmaInterpretare = "Cu potențial pentru forță și viteză (randament metabolic și funcțional foarte bun al mușchiului)";
        iFmaStatus = "potential";
      } else {
        iFmaInterpretare = "Nivel slab de pregătire";
        iFmaStatus = "slab";
      }
    } else {
      iFmaInterpretare = "Necesită completarea greutății pentru interpretare";
      iFmaStatus = "incomplete";
    }
    
    setValue("i_fma_interpretare", iFmaInterpretare);
    setValue("i_fma_status", iFmaStatus);

    setIsCalculating(false);
    toast.success("Rezultatele au fost calculate!");
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (data.completat && !data.data_completare) {
        data.data_completare = new Date().toISOString();
      }
      
      const numericFields = ['talie', 'greutate', 'bust', 'anvergura', 'pl_abdomen', 'pl_flanc', 
                            'pl_spate', 'pl_brat', 'pl_coapsa', 'f_flexori_dr', 'f_flexori_stg',
                            'f_scapulara', 'f_lombara', 'imc', 'ta_procent', 'ma_procent', 
                            'ta_kg', 'ma_kg', 'greutate_optima', 'i_flexori', 'i_scapulara',
                            'i_lombara', 'i_fg', 'i_fma'];
      
      // Forțează conversia oricărei valori numerice la float, sau 0 pentru gol/NaN/Infinity
      numericFields.forEach(field => {
        const val = data[field];
        if (val === "" || val === undefined || val === null) {
          data[field] = 0;
        } else {
          const parsed = parseFloat(val);
          data[field] = Number.isFinite(parsed) ? parsed : 0;
        }
      });
      
      const updated = await patientApi.updateAtelier1(patientId, data);
      toast.success("Atelier 1 salvat cu succes!");
      setOpen(false);
      if (onUpdate) {
        onUpdate(updated);
      }
    } catch (error) {
      console.error("Failed to update atelier:", error);
      const detail = error?.response?.data?.detail;
      if (detail) {
        const msg = typeof detail === 'string' ? detail : JSON.stringify(detail).slice(0, 200);
        toast.error(`Eroare salvare: ${msg}`);
      } else {
        toast.error("Eroare la salvarea atelierului. Verifică datele introduse.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isCompleted = atelierData?.completat;

  // Component for showing validation status
  const ValidationIcon = ({ isValid }) => {
    if (isValid) {
      return <CheckCircle className="w-5 h-5 text-emerald-500" />;
    }
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  // Component for I.F.M.A status
  const IFMAStatusIcon = ({ status }) => {
    if (status === "optim") {
      return <CheckCircle className="w-5 h-5 text-emerald-500" />;
    } else if (status === "potential") {
      return <AlertTriangle className="w-5 h-5 text-amber-500" />;
    }
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant={isCompleted ? "default" : "outline"}
          className="gap-2 flex-1"
          data-testid="atelier-1-btn"
        >
          {isCompleted ? (
            <Check className="w-4 h-4" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          Atelier 1
          {isCompleted && (
            <Badge variant="secondary" className="ml-1 text-xs">
              Completat
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            Atelier 1 - Analiza Compoziției Corporale și Forță Musculară
          </DialogTitle>
          {atelierData?.data_completare && (
            <p className="text-sm text-muted-foreground">
              Completat la: {formatDate(atelierData.data_completare)}
            </p>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mt-4">
          {/* Măsurători de bază */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Măsurători de Bază</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="talie">Talie (m)</Label>
                  <Input
                    id="talie"
                    type="number"
                    step="0.01"
                    data-testid="atelier-1-talie"
                    placeholder="ex: 1.75"
                    {...register("talie")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="greutate">Greutate (kg)</Label>
                  <Input
                    id="greutate"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-greutate"
                    placeholder="ex: 70"
                    {...register("greutate")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bust">Bust (cm) <span className="text-muted-foreground text-xs">(opțional)</span></Label>
                  <Input
                    id="bust"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-bust"
                    placeholder="ex: 90"
                    {...register("bust")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="anvergura">Anvergura (cm) <span className="text-muted-foreground text-xs">(opțional)</span></Label>
                  <Input
                    id="anvergura"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-anvergura"
                    placeholder="ex: 175"
                    {...register("anvergura")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobilitate">Mobilitate</Label>
                  <Select
                    value={watchedValues.mobilitate}
                    onValueChange={(value) => setValue("mobilitate", value)}
                  >
                    <SelectTrigger data-testid="atelier-1-mobilitate">
                      <SelectValue placeholder="Selectați" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="F. Buna">F. Bună</SelectItem>
                      <SelectItem value="Buna">Bună</SelectItem>
                      <SelectItem value="Medie">Medie</SelectItem>
                      <SelectItem value="Slaba">Slabă</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sex (din fișă)</Label>
                  <Input
                    value={sex || "Nespecificat"}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pliuri Cutanate */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Pliuri Cutanate (mm)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pl_abdomen">Pl. Abdomen</Label>
                  <Input
                    id="pl_abdomen"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-pl-abdomen"
                    {...register("pl_abdomen")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pl_flanc">Pl. Flanc</Label>
                  <Input
                    id="pl_flanc"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-pl-flanc"
                    {...register("pl_flanc")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pl_spate">Pl. Spate</Label>
                  <Input
                    id="pl_spate"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-pl-spate"
                    {...register("pl_spate")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pl_brat">Pl. Braț</Label>
                  <Input
                    id="pl_brat"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-pl-brat"
                    {...register("pl_brat")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pl_coapsa">Pl. Coapsă</Label>
                  <Input
                    id="pl_coapsa"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-pl-coapsa"
                    {...register("pl_coapsa")}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Forță Musculară — colaps automat pentru sportivi sub 13 ani */}
          <Card>
            <CardHeader className="pb-3">
              <button
                type="button"
                onClick={() => setShowForta((v) => !v)}
                className="w-full flex items-center justify-between text-left hover:opacity-80 transition"
                data-testid="atelier-1-forta-toggle"
              >
                <CardTitle className="text-base flex items-center gap-2">
                  {showForta ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Forță Musculară (kg)
                  {isUnder13 && !showForta && (
                    <span className="text-xs font-normal text-slate-500 ml-2">
                      (sub 13 ani — apasă pentru a deschide)
                    </span>
                  )}
                </CardTitle>
              </button>
            </CardHeader>
            {showForta && (
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="f_flexori_dr">F. Flexori Dr</Label>
                  <Input
                    id="f_flexori_dr"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-f-flexori-dr"
                    {...register("f_flexori_dr")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="f_flexori_stg">F. Flexori Stg</Label>
                  <Input
                    id="f_flexori_stg"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-f-flexori-stg"
                    {...register("f_flexori_stg")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="f_scapulara">F. Scapulară</Label>
                  <Input
                    id="f_scapulara"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-f-scapulara"
                    {...register("f_scapulara")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="f_lombara">F. Lombară</Label>
                  <Input
                    id="f_lombara"
                    type="number"
                    step="0.1"
                    data-testid="atelier-1-f-lombara"
                    {...register("f_lombara")}
                  />
                </div>
              </div>
            </CardContent>
            )}
          </Card>

          {/* Evaluare Posturală */}
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Evaluare Posturală
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Tulburări de statică vertebrală */}
              <div className="space-y-3 p-3 border rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">Tulburări de statică vertebrală</Label>
                  <Select
                    value={watchedValues.tulburari_statica}
                    onValueChange={(value) => setValue("tulburari_statica", value)}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue placeholder="..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Da">Da</SelectItem>
                      <SelectItem value="Nu">Nu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {watchedValues.tulburari_statica === "Da" && (
                  <div className="ml-4 space-y-2 p-3 bg-white rounded border">
                    <p className="text-sm text-muted-foreground mb-2">Selectați tipurile de tulburări:</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="tulb_atitudine_cifotica"
                          checked={watchedValues.tulb_atitudine_cifotica}
                          onCheckedChange={(checked) => setValue("tulb_atitudine_cifotica", checked)}
                        />
                        <Label htmlFor="tulb_atitudine_cifotica" className="text-sm cursor-pointer">
                          Atitudine cifotică
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="tulb_cifoza"
                          checked={watchedValues.tulb_cifoza}
                          onCheckedChange={(checked) => setValue("tulb_cifoza", checked)}
                        />
                        <Label htmlFor="tulb_cifoza" className="text-sm cursor-pointer flex items-center gap-1">
                          Cifoză {watchedValues.tulb_cifoza && <XCircle className="w-4 h-4 text-red-500" />}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="tulb_atitudine_scoliotica"
                          checked={watchedValues.tulb_atitudine_scoliotica}
                          onCheckedChange={(checked) => setValue("tulb_atitudine_scoliotica", checked)}
                        />
                        <Label htmlFor="tulb_atitudine_scoliotica" className="text-sm cursor-pointer">
                          Atitudine scoliotică
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="tulb_scolioza"
                          checked={watchedValues.tulb_scolioza}
                          onCheckedChange={(checked) => setValue("tulb_scolioza", checked)}
                        />
                        <Label htmlFor="tulb_scolioza" className="text-sm cursor-pointer flex items-center gap-1">
                          Scolioză {watchedValues.tulb_scolioza && <XCircle className="w-4 h-4 text-red-500" />}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="tulb_atitudine_cifoscoliotica"
                          checked={watchedValues.tulb_atitudine_cifoscoliotica}
                          onCheckedChange={(checked) => setValue("tulb_atitudine_cifoscoliotica", checked)}
                        />
                        <Label htmlFor="tulb_atitudine_cifoscoliotica" className="text-sm cursor-pointer">
                          Atitudine cifoscoliotică
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="tulb_cifoscolioza"
                          checked={watchedValues.tulb_cifoscolioza}
                          onCheckedChange={(checked) => setValue("tulb_cifoscolioza", checked)}
                        />
                        <Label htmlFor="tulb_cifoscolioza" className="text-sm cursor-pointer flex items-center gap-1">
                          Cifoscolioză {watchedValues.tulb_cifoscolioza && <XCircle className="w-4 h-4 text-red-500" />}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="tulb_atitudine_hiperlordotica"
                          checked={watchedValues.tulb_atitudine_hiperlordotica}
                          onCheckedChange={(checked) => setValue("tulb_atitudine_hiperlordotica", checked)}
                        />
                        <Label htmlFor="tulb_atitudine_hiperlordotica" className="text-sm cursor-pointer">
                          Atitudine hiperlordotică lombară
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="tulb_hiperlordoza"
                          checked={watchedValues.tulb_hiperlordoza}
                          onCheckedChange={(checked) => setValue("tulb_hiperlordoza", checked)}
                        />
                        <Label htmlFor="tulb_hiperlordoza" className="text-sm cursor-pointer flex items-center gap-1">
                          Hiperlordoză lombară comp. {watchedValues.tulb_hiperlordoza && <XCircle className="w-4 h-4 text-red-500" />}
                        </Label>
                      </div>
                    </div>
                    {(watchedValues.tulb_cifoza || watchedValues.tulb_scolioza || watchedValues.tulb_cifoscolioza || watchedValues.tulb_hiperlordoza) && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        <strong>Recomandare:</strong> Se recomandă kinetoterapie de corecție a posturii
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Asimetrie umeri */}
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label className="font-medium flex items-center gap-2">
                  Asimetrie umeri
                  {watchedValues.asimetrie_umeri === "Da" && <XCircle className="w-4 h-4 text-red-500" />}
                </Label>
                <Select
                  value={watchedValues.asimetrie_umeri}
                  onValueChange={(value) => setValue("asimetrie_umeri", value)}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue placeholder="..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Da">Da</SelectItem>
                    <SelectItem value="Nu">Nu</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Asimetrie torace */}
              <div className="space-y-2 p-3 border rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="font-medium flex items-center gap-2">
                    Asimetrie torace, anormal dezvoltate
                    {watchedValues.asimetrie_torace === "Da" && <XCircle className="w-4 h-4 text-red-500" />}
                  </Label>
                  <Select
                    value={watchedValues.asimetrie_torace}
                    onValueChange={(value) => setValue("asimetrie_torace", value)}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue placeholder="..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Da">Da</SelectItem>
                      <SelectItem value="Nu">Nu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {watchedValues.asimetrie_torace === "Da" && (
                  <>
                    <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                      <strong>Diagnostic:</strong> Sechele rahitism
                    </div>
                    <div className="mt-2 ml-1 space-y-1.5 p-2 bg-slate-50 border rounded" data-testid="asimetrie-torace-recomandari">
                      <p className="text-xs font-semibold text-slate-600 mb-1">
                        Recomandări (bifate → apar automat în Concluzii aviz medico-sportiv):
                      </p>
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id="at_dozare_vit_d"
                          checked={!!watchedValues.at_dozare_vit_d}
                          onCheckedChange={(v) => setValue("at_dozare_vit_d", !!v)}
                          data-testid="at-dozare-vit-d"
                        />
                        <Label htmlFor="at_dozare_vit_d" className="text-xs cursor-pointer leading-snug">
                          Dozarea 25-OH-Vitamine D serice. În caz de deficit/insuficiență, inițierea substituției cu Vitamina D3 (conform ghidurilor în vigoare) și dispensarizare prin Medicul de Familie/Pediatru. Program KT pentru corectarea/compensarea deficiențelor posturale secundare
                        </Label>
                      </div>
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id="at_activitate_adaptata"
                          checked={!!watchedValues.at_activitate_adaptata}
                          onCheckedChange={(v) => setValue("at_activitate_adaptata", !!v)}
                          data-testid="at-activitate-adaptata"
                        />
                        <Label htmlFor="at_activitate_adaptata" className="text-xs cursor-pointer leading-snug">
                          Activitate fizică adaptată cu accent pe exerciții pe postură, se evită supraîncărcările articulare extreme până la normalizarea nivelului vitaminei D
                        </Label>
                      </div>
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id="at_optimizare_calciu"
                          checked={!!watchedValues.at_optimizare_calciu}
                          onCheckedChange={(v) => setValue("at_optimizare_calciu", !!v)}
                          data-testid="at-optimizare-calciu"
                        />
                        <Label htmlFor="at_optimizare_calciu" className="text-xs cursor-pointer leading-snug">
                          Optimizarea raportului alimentar de calciu
                        </Label>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Asimetrie membre inferioare */}
              <div className="space-y-2 p-3 border rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">Asimetrie membre inferioare</Label>
                  <Select
                    value={watchedValues.asimetrie_membre_inf}
                    onValueChange={(value) => setValue("asimetrie_membre_inf", value)}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue placeholder="..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Da">Da</SelectItem>
                      <SelectItem value="Nu">Nu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {watchedValues.asimetrie_membre_inf === "Da" && (
                  <div className="ml-4 space-y-2 w-full">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">Membru stâng</Label>
                        <Select
                          value={watchedValues.asimetrie_membre_inf_stang}
                          onValueChange={(value) => setValue("asimetrie_membre_inf_stang", value)}
                        >
                          <SelectTrigger className="w-full" data-testid="asim-membru-stang">
                            <SelectValue placeholder="Selectați tipul" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Mica">Mică</SelectItem>
                            <SelectItem value="Semnificativa">Semnificativă (&gt;2cm)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">Membru drept</Label>
                        <Select
                          value={watchedValues.asimetrie_membre_inf_drept}
                          onValueChange={(value) => setValue("asimetrie_membre_inf_drept", value)}
                        >
                          <SelectTrigger className="w-full" data-testid="asim-membru-drept">
                            <SelectValue placeholder="Selectați tipul" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Mica">Mică</SelectItem>
                            <SelectItem value="Semnificativa">Semnificativă (&gt;2cm)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {(watchedValues.asimetrie_membre_inf_stang === "Semnificativa" ||
                      watchedValues.asimetrie_membre_inf_drept === "Semnificativa") && (
                      <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                        <XCircle className="w-4 h-4" />
                        <span><strong>Recomandare:</strong> Se recomandă control Ortopedie Pediatrică</span>
                      </div>
                    )}
                    {watchedValues.asimetrie_membre_inf_stang === "Semnificativa" && (
                      <div className="flex items-start gap-2 p-2 rounded-md border bg-slate-50" data-testid="mi-stang-mai-mare-row">
                        <Checkbox
                          id="mi-stang-mai-mare"
                          checked={!!watchedValues.mi_stang_mai_mare}
                          onCheckedChange={(v) => setValue("mi_stang_mai_mare", !!v)}
                          data-testid="mi-stang-mai-mare-check"
                        />
                        <Label htmlFor="mi-stang-mai-mare" className="text-sm cursor-pointer">
                          M.I stâng mai mare decât M.I drept
                        </Label>
                      </div>
                    )}
                    {watchedValues.asimetrie_membre_inf_drept === "Semnificativa" && (
                      <div className="flex items-start gap-2 p-2 rounded-md border bg-slate-50" data-testid="mi-drept-mai-mare-row">
                        <Checkbox
                          id="mi-drept-mai-mare"
                          checked={!!watchedValues.mi_drept_mai_mare}
                          onCheckedChange={(v) => setValue("mi_drept_mai_mare", !!v)}
                          data-testid="mi-drept-mai-mare-check"
                        />
                        <Label htmlFor="mi-drept-mai-mare" className="text-sm cursor-pointer">
                          M.I drept mai mare decât M.I stâng
                        </Label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Genu valgum */}
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label className="font-medium flex items-center gap-2">
                  Genu valgum
                  {watchedValues.genu_valgum === "Da" && <XCircle className="w-4 h-4 text-red-500" />}
                </Label>
                <Select
                  value={watchedValues.genu_valgum}
                  onValueChange={(value) => setValue("genu_valgum", value)}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue placeholder="..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Da">Da</SelectItem>
                    <SelectItem value="Nu">Nu</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Genu varum */}
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label className="font-medium flex items-center gap-2">
                  Genu varum
                  {watchedValues.genu_varum === "Da" && <XCircle className="w-4 h-4 text-red-500" />}
                </Label>
                <Select
                  value={watchedValues.genu_varum}
                  onValueChange={(value) => setValue("genu_varum", value)}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue placeholder="..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Da">Da</SelectItem>
                    <SelectItem value="Nu">Nu</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Picior addus */}
              <div className="space-y-2 p-3 border rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="font-medium flex items-center gap-2">
                    Picior addus
                    {watchedValues.picior_addus === "Da" && <XCircle className="w-4 h-4 text-red-500" />}
                  </Label>
                  <Select
                    value={watchedValues.picior_addus}
                    onValueChange={(value) => setValue("picior_addus", value)}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue placeholder="..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Da">Da</SelectItem>
                      <SelectItem value="Nu">Nu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {watchedValues.picior_addus === "Da" && (
                  <div className="ml-4 space-y-2">
                    <Select
                      value={watchedValues.picior_addus_grad}
                      onValueChange={(value) => setValue("picior_addus_grad", value)}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Selectați gradul" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Gradul 1">Gradul 1</SelectItem>
                        <SelectItem value="Gradul 2">Gradul 2</SelectItem>
                        <SelectItem value="Gradul 3">Gradul 3</SelectItem>
                      </SelectContent>
                    </Select>
                    {(watchedValues.picior_addus_grad === "Gradul 2" || watchedValues.picior_addus_grad === "Gradul 3") && (
                      <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                        <XCircle className="w-4 h-4" />
                        <span><strong>Recomandare:</strong> Se recomandă kinetoterapie și ortezare de corecție</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Recomandări prestabilite — mereu vizibile, indiferent de anomaliile bifate */}
              <div
                className="space-y-2 p-3 rounded-md border border-amber-200 bg-amber-50/40"
                data-testid="posturale-recs-picker"
              >
                  <Label className="text-sm font-semibold flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    Recomandări prestabilite (click pentru adăugare)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Bifează pentru a adăuga automat în câmpul „Alte recomandări posturale”. Poți debifa pentru a le elimina.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 pt-1">
                    {[
                      "Program KT supravegheat pentru corectie a posturii",
                      "Exercitii de extensie a spatelui",
                      "Exercitii de tonifiere musculara paravertebrala si abdominala si extensie a spatelui",
                      "Program KT corectiv pentru membrele inferioare",
                      "Analiza baropodometrica",
                      "Monitorizarea posturii",
                    ].map((rec) => {
                      const current = (watchedValues.recomandari_posturale || "").trim();
                      const lines = current ? current.split("\n").map((l) => l.trim()) : [];
                      const checked = lines.includes(rec);
                      const toggle = () => {
                        if (checked) {
                          const next = lines.filter((l) => l !== rec).join("\n");
                          setValue("recomandari_posturale", next, { shouldDirty: true });
                        } else {
                          const next = current ? `${current}\n${rec}` : rec;
                          setValue("recomandari_posturale", next, { shouldDirty: true });
                        }
                      };
                      const testId = `posturale-rec-${rec.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`;
                      return (
                        <label
                          key={rec}
                          className="flex items-start gap-2 text-sm py-1 px-2 rounded hover:bg-white/60 cursor-pointer"
                          data-testid={testId}
                        >
                          <Checkbox checked={checked} onCheckedChange={toggle} className="mt-0.5" />
                          <span className="leading-tight">{rec}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

              {/* Recomandări posturale adiționale */}
              <div className="space-y-2">
                <Label htmlFor="recomandari_posturale">Alte recomandări posturale</Label>
                <Textarea
                  id="recomandari_posturale"
                  rows={3}
                  placeholder="Adăugați recomandări suplimentare..."
                  {...register("recomandari_posturale")}
                />
              </div>
            </CardContent>
          </Card>

          {/* Buton Generare */}
          <div className="flex justify-center">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={calculateResults}
              disabled={isCalculating}
              className="gap-2"
              data-testid="atelier-1-generate"
            >
              {isCalculating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Calculator className="w-4 h-4" />
              )}
              Generează Rezultate
            </Button>
          </div>

          <Separator />

          {/* Rezultate Calculate - Compoziție Corporală */}
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="w-4 h-4 text-primary" />
                Rezultate - Compoziție Corporală
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="imc">I.M.C.</Label>
                  <Input
                    id="imc"
                    type="number"
                    step="0.01"
                    data-testid="atelier-1-imc"
                    className="bg-white font-semibold"
                    {...register("imc")}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="imc_interpretare">Interpretare IMC</Label>
                  <Input
                    id="imc_interpretare"
                    data-testid="atelier-1-imc-interpretare"
                    className="bg-white"
                    {...register("imc_interpretare")}
                  />
                  {age && age < 18 && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Interpretat conform graficelor OMS pentru vârsta {age} ani
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ta_procent">TA%</Label>
                  <Input
                    id="ta_procent"
                    type="number"
                    step="0.01"
                    className="bg-white font-semibold"
                    {...register("ta_procent")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ma_procent">MA%</Label>
                  <Input
                    id="ma_procent"
                    type="number"
                    step="0.01"
                    className="bg-white font-semibold"
                    {...register("ma_procent")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ta_kg">TA (kg)</Label>
                  <Input
                    id="ta_kg"
                    type="number"
                    step="0.01"
                    className="bg-white"
                    {...register("ta_kg")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ma_kg">MA (kg)</Label>
                  <Input
                    id="ma_kg"
                    type="number"
                    step="0.01"
                    className="bg-white"
                    {...register("ma_kg")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="greutate_optima">Greutate Optimă (kg)</Label>
                  <Input
                    id="greutate_optima"
                    type="number"
                    step="0.01"
                    className="bg-white font-semibold text-primary"
                    {...register("greutate_optima")}
                  />
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <Label htmlFor="recomandare_sport">
                  Recomandare pentru {sport || "sportul practicat"}
                </Label>
                <Textarea
                  id="recomandare_sport"
                  rows={3}
                  className="bg-white"
                  placeholder="Recomandarea va fi generată automat..."
                  {...register("recomandare_sport")}
                />
              </div>

              <div className="mt-4 space-y-2">
                <Label htmlFor="recomandare_nutritie" className="flex items-center gap-2">
                  <span>🥗 Recomandare nutriție</span>
                  <span className="text-xs text-muted-foreground font-normal">(generată automat în funcție de IMC și vârstă)</span>
                </Label>
                <Textarea
                  id="recomandare_nutritie"
                  data-testid="recomandare-nutritie"
                  rows={4}
                  className="bg-white"
                  placeholder="Se va completa automat la calcularea IMC-ului..."
                  {...register("recomandare_nutritie")}
                />
              </div>
            </CardContent>
          </Card>

          {/* Rezultate Calculate - Indici Forță */}
          <Card className="border-emerald-500/20 bg-emerald-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="w-4 h-4 text-emerald-600" />
                Rezultate - Indici Forță Musculară
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* I.Flexori */}
                <div className="space-y-2">
                  <Label htmlFor="i_flexori" className="flex items-center gap-2">
                    I.Flexori
                    {watchedValues.i_flexori && (
                      <ValidationIcon isValid={watchedValues.i_flexori_valid} />
                    )}
                  </Label>
                  <Input
                    id="i_flexori"
                    type="number"
                    step="0.01"
                    className="bg-white"
                    {...register("i_flexori")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Referință: {sex === "Masculin" ? "≥65%" : "≥50%"}
                  </p>
                </div>

                {/* I.Scapulara */}
                <div className="space-y-2">
                  <Label htmlFor="i_scapulara" className="flex items-center gap-2">
                    I.Scapulară
                    {watchedValues.i_scapulara && (
                      <ValidationIcon isValid={watchedValues.i_scapulara_valid} />
                    )}
                  </Label>
                  <Input
                    id="i_scapulara"
                    type="number"
                    step="0.01"
                    className="bg-white"
                    {...register("i_scapulara")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Referință: {sex === "Masculin" ? "≥55%" : "≥40%"}
                  </p>
                </div>

                {/* I.Lombara */}
                <div className="space-y-2">
                  <Label htmlFor="i_lombara" className="flex items-center gap-2">
                    I.Lombară
                    {watchedValues.i_lombara && (
                      <ValidationIcon isValid={watchedValues.i_lombara_valid} />
                    )}
                  </Label>
                  <Input
                    id="i_lombara"
                    type="number"
                    step="0.01"
                    className="bg-white"
                    {...register("i_lombara")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Referință: {sex === "Masculin" ? "≥190%" : "≥130%"}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* I.F.G */}
                <div className="space-y-2">
                  <Label htmlFor="i_fg">I.F.G (Indice Forță Generală)</Label>
                  <Input
                    id="i_fg"
                    type="number"
                    step="0.01"
                    className="bg-white font-semibold"
                    {...register("i_fg")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Referință: ≥1.0
                  </p>
                </div>

                {/* I.F.M.A */}
                <div className="space-y-2">
                  <Label htmlFor="i_fma">I.F.M.A (Indice Forță / Masă Activă)</Label>
                  <Input
                    id="i_fma"
                    type="number"
                    step="0.01"
                    className="bg-white font-semibold"
                    {...register("i_fma")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Referință: ≥1.2
                  </p>
                </div>
              </div>

              {/* Interpretare I.F.M.A */}
              {watchedValues.i_fma_interpretare && (
                <div className={`p-4 rounded-lg flex items-start gap-3 ${
                  watchedValues.i_fma_status === "optim" 
                    ? "bg-emerald-100 border border-emerald-300" 
                    : watchedValues.i_fma_status === "potential"
                    ? "bg-amber-100 border border-amber-300"
                    : "bg-red-100 border border-red-300"
                }`}>
                  <IFMAStatusIcon status={watchedValues.i_fma_status} />
                  <div>
                    <p className={`font-medium ${
                      watchedValues.i_fma_status === "optim" 
                        ? "text-emerald-800" 
                        : watchedValues.i_fma_status === "potential"
                        ? "text-amber-800"
                        : "text-red-800"
                    }`}>
                      {watchedValues.i_fma_interpretare}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Observații */}
          <div className="space-y-2">
            <Label htmlFor="observatii-1">Observații Suplimentare</Label>
            <Textarea
              id="observatii-1"
              data-testid="atelier-1-observatii"
              rows={3}
              placeholder="Adăugați observații suplimentare..."
              {...register("observatii")}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-4 border-t">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="completat-1"
                data-testid="atelier-1-completat"
                checked={completat}
                onCheckedChange={(checked) => setValue("completat", checked)}
              />
              <Label htmlFor="completat-1" className="text-sm font-medium cursor-pointer">
                Marchează ca și completat
              </Label>
            </div>
            <div className="flex-1" />
            <GenerateVizaButton patient={patientData} onGenerated={onUpdate} />
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Anulează
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              data-testid="atelier-1-save"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvează
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
